/**
 * Firebase の初期化(唯一の入口)。
 * - APIキーのハードコード禁止。値はすべて .env(VITE_FIREBASE_*)から読む
 * - 開発は常に yojitsu-dev。本番プロジェクトへの接続は VITE_APP_ENV=prod のビルドのみ
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  browserSessionPersistence,
  indexedDBLocalPersistence,
  connectAuthEmulator,
  initializeAuth,
  type Auth,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';

const REQUIRED_KEYS = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

function readConfig() {
  const missing = REQUIRED_KEYS.filter((k) => !import.meta.env[k]);
  if (missing.length > 0) {
    throw new Error(
      `.env の設定が不足しています: ${missing.join(', ')}\n` +
        '.env.example をコピーして .env を作り、Firebaseコンソールの値を入れてください。',
    );
  }
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };
}

export const appEnv: 'dev' | 'prod' = import.meta.env.VITE_APP_ENV === 'prod' ? 'prod' : 'dev';

/** 副次アプリ(ユーザー作成用)の初期化に使う。通常の画面からは呼ばない */
export function getFirebaseConfig() {
  return readConfig();
}

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;
let _storage: FirebaseStorage | null = null;

function ensureApp(): FirebaseApp {
  if (_app) return _app;
  const config = readConfig();

  // 事故防止: dev ビルドで本番プロジェクトに繋がっていたら止める
  if (appEnv === 'dev' && config.projectId.includes('prod')) {
    throw new Error(
      `開発ビルドが本番プロジェクト(${config.projectId})に接続しようとしています。.env を確認してください。`,
    );
  }

  _app = initializeApp(config);

  if (import.meta.env.VITE_USE_EMULATOR === 'true') {
    connectAuthEmulator(getFirebaseAuth(), 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(getFirestore(_app), '127.0.0.1', 8080);
    connectStorageEmulator(getStorage(_app), '127.0.0.1', 9199);
  }
  return _app;
}

/** Cloud Functions の呼び出しなどで使う(2026-09-03) */
export function getFirebaseApp(): FirebaseApp {
  return ensureApp();
}

export function getFirebaseAuth(): Auth {
  if (!_auth) {
    // ログインの保存先(2026-08-26):
    // - テスト段階の既定は**タブ単位(sessionStorage)**。同じブラウザで複数ユーザーの
    //   同時テストができる(タブごとに別ユーザーでログイン可能)
    // - 本番は VITE_AUTH_PERSISTENCE=local を設定すると、タブを閉じても・再訪しても
    //   ログインしたままになる(IndexedDBに保存)
    // - 本番運用開始(2026-08-31): 配信ビルドは既定で「ログインしたまま」にする。
    //   ローカル開発(npm run dev)はタブ単位のままなので、複数ユーザーの同時テストができる
    // 説明会・検証用(2026-09-03): URLに ?tab を付けて開いたタブは、ログインをそのタブだけに保つ。
    // 同じブラウザの別タブに別のアカウントで入れる(本番URLのまま使える)。
    // 一度 ?tab で開けば、そのタブ内では画面を移動・再読み込みしても維持される
    const tabScoped = (() => {
      try {
        // テスト環境(ブランチのプレビュー配信)は、別タブに別アカウントで入れるよう常にタブ単位にする(2026-09-07)
        const host = window.location.hostname;
        const previewHost = host.endsWith('.pages.dev') && host !== 'yojitsu-system.pages.dev';
        if (previewHost || new URLSearchParams(window.location.search).has('tab')) sessionStorage.setItem('auth-scope', 'tab');
        return sessionStorage.getItem('auth-scope') === 'tab';
      } catch {
        return false;
      }
    })();
    const persistLocal =
      !tabScoped &&
      (import.meta.env.PROD || (import.meta.env.VITE_AUTH_PERSISTENCE as string | undefined) === 'local');
    _auth = initializeAuth(ensureApp(), {
      persistence: persistLocal ? indexedDBLocalPersistence : browserSessionPersistence,
    });
  }
  return _auth;
}

export function getDb(): Firestore {
  if (!_db) _db = getFirestore(ensureApp());
  return _db;
}

export function getFirebaseStorage(): FirebaseStorage {
  // 確認用テスト環境は当面 Storage なし(VITE_STORAGE_DISABLED=true でビルド・2026-10-22 発注者判断)。
  // 添付・受領書・精算PDFの保存と表示は、分かる言葉で失敗させる(Storage を用意したら設定を外す)
  if (import.meta.env.VITE_STORAGE_DISABLED === 'true') {
    throw new Error('この環境では添付ファイルを使えません(確認用テスト環境は添付ファイルの保存場所を用意していません)');
  }
  if (!_storage) _storage = getStorage(ensureApp());
  return _storage;
}

/** 接続先プロジェクトID(画面表示・確認用) */
export function getProjectId(): string {
  return ensureApp().options.projectId ?? '(不明)';
}
